import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';
import api from '../services/api';

export default function Auth({ onAuth, initialMode = 'login', onClose, standalone = false }) {
  const [mode,       setMode]       = useState(initialMode);
  const [form,       setForm]       = useState({ name: '', email: '', phone: '', password: '' });
  const [notifyPref, setNotifyPref] = useState('email');
  const [smsConsent, setSmsConsent] = useState(false);
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { toast } = useToast();

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (mode === 'forgot') {
        await api.forgotPassword(form.email);
        setForgotSent(true);
      } else if (mode === 'signup') {
        const data = await api.signup(form.name, form.email, form.phone, form.password, notifyPref, smsConsent);
        toast('Account created! Welcome to Cinema Alert.', 'success');
        onAuth(data.user);
      } else {
        const data = await api.login(form.email, form.password);
        toast(`Welcome back, ${data.user.name.split(' ')[0]}!`, 'success');
        onAuth(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!onClose) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  function switchMode(m) { setMode(m); setError(''); setForgotSent(false); }

  return (
    <div className={standalone ? 'auth-standalone-page' : 'auth-page'}>
      <div className="auth-wrap">
        <div className="auth-card">
          {/* Drag handle — visible on mobile bottom sheet */}
          <div className="auth-drag-handle" aria-hidden="true" />

          {/* Close button — top-right corner of the card (not shown on standalone page) */}
          {onClose && !standalone && (
            <button
              className="auth-close"
              onClick={onClose}
              type="button"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 2L2 14M2 2l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}

          {/* Brand — inside the card so it is never lost against the dark page */}
          <div className="auth-brand">
            <div className="auth-brand-icon">🎬</div>
            <h1>Cinema<span> Alert</span></h1>
            <p>
              {mode === 'signup'
                ? 'Create your account and never miss a ticket drop.'
                : mode === 'forgot'
                ? "Enter your email and we'll send you a reset link."
                : 'Welcome back — sign in to manage your alerts.'}
            </p>
          </div>

          {/* Tab toggle */}
          <div className="auth-tabs">
            <button
              className={`auth-tab${mode === 'login' || mode === 'forgot' ? ' active' : ''}`}
              onClick={() => switchMode('login')}
              type="button"
            >
              Sign In
            </button>
            <button
              className={`auth-tab${mode === 'signup' ? ' active' : ''}`}
              onClick={() => switchMode('signup')}
              type="button"
            >
              Create Account
            </button>
          </div>

          {/* Perks (signup only) */}
          {mode === 'signup' && (
            <div className="auth-perks">
              {[
                'Instant SMS alert the moment tickets go on sale',
                'Email notification with direct ticket links',
                'Tracks Megaplex and Cinemark across Utah',
              ].map(p => (
                <div className="auth-perk" key={p}>
                  <span className="auth-perk-icon">✓</span>
                  <span>{p}</span>
                </div>
              ))}
            </div>
          )}

          {/* Forgot password — success state */}
          {mode === 'forgot' && forgotSent ? (
            <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📬</div>
              <p style={{ color: '#ffffff', fontWeight: 700, fontSize: 15, margin: '0 0 8px' }}>Check your inbox</p>
              <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>
                If an account exists for <strong style={{ color: '#ffffff' }}>{form.email}</strong>,
                you'll receive a reset link shortly. It expires in 1 hour.
              </p>
              <button
                type="button"
                onClick={() => switchMode('login')}
                style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <>
              {/* Form */}
              <form onSubmit={handleSubmit}>
                {mode === 'signup' && (
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input
                      className="form-input"
                      name="name"
                      type="text"
                      placeholder="John Smith"
                      value={form.name}
                      onChange={handleChange}
                      required
                      autoFocus
                    />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    className="form-input"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoFocus={mode === 'login' || mode === 'forgot'}
                  />
                </div>

                {mode === 'signup' && (
                  <>
                    {/* Notification preference selector */}
                    <div className="form-group">
                      <label className="form-label">How would you like to be notified?</label>
                      <div className="notif-pref-row">
                        {[
                          { value: 'email', label: '📧 Email' },
                          { value: 'sms',   label: '📱 Text' },
                          { value: 'both',  label: '📧📱 Both' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setNotifyPref(opt.value)}
                            className={`notif-pref-btn${notifyPref === opt.value ? ' active' : ''}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Phone — required for SMS/Both, optional for Email */}
                    <div className="form-group">
                      <label className="form-label">
                        Phone Number{notifyPref === 'email' && <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 400, fontSize: 12 }}> (optional)</span>}
                      </label>
                      <input
                        className="form-input"
                        name="phone"
                        type="tel"
                        placeholder="+1 (801) 555-0123"
                        value={form.phone}
                        onChange={handleChange}
                        required={notifyPref !== 'email'}
                      />
                    </div>
                  </>
                )}

                {(mode === 'login' || mode === 'signup') && (
                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                      <label className="form-label" style={{ margin: 0 }}>Password</label>
                      {mode === 'login' && (
                        <button
                          type="button"
                          onClick={() => switchMode('forgot')}
                          style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: 12, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                        >
                          Forgot Password?
                        </button>
                      )}
                    </div>
                    <input
                      className="form-input"
                      name="password"
                      type="password"
                      placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
                      value={form.password}
                      onChange={handleChange}
                      required
                    />
                  </div>
                )}

                {mode === 'signup' && (
                  <div style={{ margin: '8px 0 2px' }}>
                    {/* SMS opt-in consent — Twilio A2P 10DLC compliant */}
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={smsConsent}
                        onChange={e => setSmsConsent(e.target.checked)}
                        style={{ marginTop: 2, flexShrink: 0, accentColor: '#7c3aed', width: 14, height: 14, cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 1.45 }}>
                        I consent to receive SMS notifications and alerts from CinemaAlert. Message frequency may vary. Message and data rates may apply. Text HELP for assistance. Reply STOP to unsubscribe at any time. See our <a href="/privacy-policy" style={{ color: '#7c3aed', textDecoration: 'underline' }}>Privacy Policy</a> and <a href="/terms" style={{ color: '#7c3aed', textDecoration: 'underline' }}>Terms of Service</a>.
                      </span>
                    </label>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: '6px 0 0', lineHeight: 1.45 }}>
                      By signing up, you agree to our{' '}
                      <a href="/terms" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}>Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy-policy" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'underline' }}>Privacy Policy</a>.
                    </p>
                  </div>
                )}

                {error && <div className="form-error">{error}</div>}

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%', marginTop: 6, padding: '10px', fontSize: 14, borderRadius: 'var(--r)' }}
                  disabled={loading}
                >
                  {loading
                    ? 'Please wait…'
                    : mode === 'login' ? 'Sign In'
                    : mode === 'signup' ? 'Create Account'
                    : 'Send Reset Link'}
                </button>
              </form>

              <div className="auth-toggle">
                {mode === 'forgot' ? (
                  <>Remember your password?{' '}
                    <button onClick={() => switchMode('login')}>Sign in</button>
                  </>
                ) : mode === 'login' ? (
                  <>Don&apos;t have an account?{' '}
                    <button onClick={() => switchMode('signup')}>Sign up free</button>
                  </>
                ) : (
                  <>Already have an account?{' '}
                    <button onClick={() => switchMode('login')}>Sign in</button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
